import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { adminUsersApi } from '../services/adminUsersApi';
import {
  GarageDetailResponse,
  GarageDocumentResponse,
  GarageMemberResponse,
  GarageServiceOfferingResponse,
} from '../types';
import { useAuth } from '../hooks/useAuth';
import SettlementConfigForm from '../components/settlements/SettlementConfigForm';
import AwaitingTabbyCard from '../components/settlements/AwaitingTabbyCard';
import RunSettlementCard from '../components/settlements/RunSettlementCard';
import EditGarageProfileDialog from '../components/EditGarageProfileDialog';
import GoogleListingSection from '../components/GoogleListingSection';
import EditGarageHoursDialog from '../components/EditGarageHoursDialog';
import AddGarageMemberDialog from '../components/AddGarageMemberDialog';
import MemberNotificationBadges from '../components/MemberNotificationBadges';
import UsernameChip from '../components/UsernameChip';
import EditGarageBankDetailsDialog from '../components/EditGarageBankDetailsDialog';
import EditGarageVatDialog from '../components/EditGarageVatDialog';
// Replaced by the shared V2 wizard in the admin services tab (Spec 046 follow-up).
// Legacy V1 dialogs kept around for back-compat references — wizard handles the new flow.
import AddServiceModalV2 from '../components/services/AddServiceModalV2';
import SignedAgreementsCard from '../components/garages/SignedAgreementsCard';
import TermsStatusCard from '../components/garages/TermsStatusCard';
import GaragePackagesTab from '../components/packages/GaragePackagesTab';
import { formatHours } from '../utils/garageHours';


const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

type TabKey = 'info' | 'members' | 'documents' | 'media' | 'hours' | 'services' | 'packages' | 'settlement';

function StatusBadge({ status }: { status: string }) {
  const cls = status === 'PENDING' ? 'pending'
    : status === 'APPROVED' ? 'approved'
    : status === 'REJECTED' ? 'rejected'
    : status === 'UPDATE_REQUESTED' ? 'update-requested'
    : status === 'INACTIVE' ? 'rejected'
    : 'pending';
  return <span className={`admin-badge ${cls}`}>{status.replace('_', ' ')}</span>;
}

function BlockedBadge() {
  return (
    <span
      className="admin-badge rejected"
      style={{ background: '#7f1d1d', color: '#fff', borderColor: '#7f1d1d' }}
    >
      BLOCKED
    </span>
  );
}

function OfferingStatusBadge({ status, blockedAt }: { status: string; blockedAt: string | null }) {
  if (blockedAt) return <BlockedBadge />;
  const cls = status === 'ACTIVE' ? 'approved'
    : status === 'INACTIVE' ? 'rejected'
    : status === 'PENDING_APPROVAL' ? 'pending'
    : status === 'REJECTED' ? 'rejected'
    : 'pending';
  return <span className={`admin-badge ${cls}`}>{status.replace('_', ' ')}</span>;
}

function Field({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div className="admin-field">
      <div className="admin-field-label">{label}</div>
      <div className="admin-field-value">{value ?? '-'}</div>
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '0.625rem 1rem',
        background: 'none',
        border: 'none',
        borderBottom: active ? '2px solid var(--admin-primary, #030c54)' : '2px solid transparent',
        color: active ? 'var(--admin-text, #030c54)' : 'var(--admin-text-muted, #6b7280)',
        fontWeight: active ? 600 : 500,
        fontSize: '0.875rem',
        cursor: 'pointer',
        marginBottom: '-1px',
      }}
    >
      {children}
    </button>
  );
}

export default function GarageDetailPage() {
  const auth = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [garage, setGarage] = useState<GarageDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [activeTab, setActiveTab] = useState<TabKey>('info');

  // Action form states
  const [activeAction, setActiveAction] = useState<'reject' | 'request-update' | null>(null);
  const [actionText, setActionText] = useState('');

  // Terms agreements

  // Garage members
  const [members, setMembers] = useState<GarageMemberResponse[]>([]);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [navigatingMemberSubject, setNavigatingMemberSubject] = useState<string | null>(null);

  // Spec 074 — bumps to reload the Signed agreements card after a terms notice send / refresh.
  const [termsRefreshKey, setTermsRefreshKey] = useState(0);
  // Documents archive
  const [documents, setDocuments] = useState<GarageDocumentResponse[]>([]);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  const [docActionId, setDocActionId] = useState<string | null>(null);
  const [rejectingDocId, setRejectingDocId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // Services tab
  const [services, setServices] = useState<GarageServiceOfferingResponse[]>([]);
  const [servicesError, setServicesError] = useState<string | null>(null);
  const [serviceActionId, setServiceActionId] = useState<string | null>(null);
  const [rejectingOfferingId, setRejectingOfferingId] = useState<string | null>(null);
  const [offeringRejectReason, setOfferingRejectReason] = useState('');

  // Spec 038 — admin edit dialogs. Spec 050 retired the separate GARAGE_EDITOR
  // write-gate: reaching this screen at all now implies edit rights.
  const canEdit = auth.hasScreen('garages');
  const [editDialog, setEditDialog] = useState<'profile' | 'hours' | 'addMember' | 'bank' | 'vat' | 'addOffering' | null>(null);
  const [editingOffering, setEditingOffering] = useState<GarageServiceOfferingResponse | null>(null);
  const [uploadBusy, setUploadBusy] = useState<string | null>(null);

  // Two-step admin upload helper used by Documents + Media tabs.
  // Step 1: get a presigned PUT URL from the gateway.
  // Step 2: PUT the file directly to S3 (no auth header — the URL is signed).
  // Step 3: confirm the upload (creates the DB row, supersedes singletons, auto-APPROVED for docs).
  const adminUploadAndConfirm = async (
    file: File,
    mediaType: 'TRADE_LICENSE' | 'OTHER_DOCUMENT' | 'LOGO' | 'THUMBNAIL' | 'GALLERY_PHOTO' | 'SERVICE_IMAGE',
    documentType?: string,
  ): Promise<void> => {
    if (!auth.token || !id) return;
    const tag = documentType ? `doc:${documentType}` : `media:${mediaType}`;
    setUploadBusy(tag);
    try {
      const { uploadUrl, storageKey } = await api.adminGarageMediaUploadUrl(id, {
        mediaType,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        fileSize: file.size,
      }, auth.token);

      const putResp = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!putResp.ok) throw new Error(`S3 upload failed: ${putResp.status}`);

      const confirmBody = {
        mediaType,
        storageKey,
        originalFilename: file.name,
        contentType: file.type || 'application/octet-stream',
        fileSize: file.size,
      };

      if (documentType) {
        await api.adminGarageDocumentUpload(id, documentType, confirmBody, auth.token);
      } else {
        await api.adminGarageMediaConfirm(id, confirmBody, auth.token);
      }
      await reloadGarage();
      // Refresh per-tab data
      if (documentType) {
        const docs = await api.listGarageDocuments(id, auth.token);
        setDocuments(docs);
      }
    } catch (err) {
      // Surface error to the right tab's error slot
      const msg = err instanceof Error ? err.message : 'Upload failed';
      if (documentType) setDocumentsError(msg);
      else setMembersError(msg);
    } finally {
      setUploadBusy(null);
    }
  };

  const [mediaReordering, setMediaReordering] = useState(false);

  /**
   * Move a gallery photo one place. Reorder is presentation, so it applies immediately rather
   * than going through the change-request gate a content edit would.
   */
  const moveGalleryPhoto = async (
    gallery: Array<{ id: string }>,
    index: number,
    delta: number,
  ) => {
    if (!auth.token || !id || mediaReordering) return;
    const to = index + delta;
    if (to < 0 || to >= gallery.length) return;
    const next = [...gallery];
    [next[index], next[to]] = [next[to], next[index]];

    setMediaReordering(true);
    try {
      await api.adminReorderGarageMedia(id, next.map((m) => m.id), auth.token);
      await reloadGarage();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to reorder photos');
    } finally {
      setMediaReordering(false);
    }
  };

  const handleDeleteMedia = async (mediaId: string) => {
    if (!auth.token || !id) return;
    if (!window.confirm('Delete this media item?')) return;
    try {
      await api.adminGarageMediaDelete(id, mediaId, auth.token);
      await reloadGarage();
    } catch (err) {
      // No dedicated media-error state; surface inline
      window.alert(err instanceof Error ? err.message : 'Failed to delete media');
    }
  };

  const handleRemoveMember = async (memberId: string, memberName: string | undefined) => {
    if (!auth.token || !id) return;
    if (!window.confirm(`Remove ${memberName ?? 'this member'} from the garage?`)) return;
    try {
      await api.adminGarageRemoveMember(id, memberId, auth.token);
      const refreshed = await api.getGarageMembers(id, auth.token);
      setMembers(refreshed);
    } catch (err) {
      setMembersError(err instanceof Error ? err.message : 'Failed to remove member');
    }
  };

  const reloadGarage = async () => {
    if (!auth.token || !id) return;
    try {
      const fresh = await api.getGarageById(id, auth.token);
      setGarage(fresh);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reload garage');
    }
  };

  const reloadServices = async () => {
    if (!auth.token || !id) return;
    try {
      const list = await api.getGarageServices(id, auth.token);
      setServices(list);
    } catch (err) {
      setServicesError(err instanceof Error ? err.message : 'Failed to load services');
    }
  };

  useEffect(() => {
    if (!auth.token || !id) return;
    api.getGarageById(id, auth.token)
      .then(setGarage)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
    api.getGarageMembers(id, auth.token)
      .then(setMembers)
      .catch((err) => setMembersError(err instanceof Error ? err.message : 'Failed to load members'));
    api.listGarageDocuments(id, auth.token)
      .then(setDocuments)
      .catch((err) => setDocumentsError(err instanceof Error ? err.message : 'Failed to load documents'));
    api.getGarageServices(id, auth.token)
      .then(setServices)
      .catch((err) => setServicesError(err instanceof Error ? err.message : 'Failed to load services'));
  }, [auth.token, id]);

  const reloadDocuments = async () => {
    if (!auth.token || !id) return;
    try {
      const docs = await api.listGarageDocuments(id, auth.token);
      setDocuments(docs);
    } catch (err) {
      setDocumentsError(err instanceof Error ? err.message : 'Failed to reload documents');
    }
  };

  const handleApproveDocument = async (documentId: string) => {
    if (!auth.token) return;
    setDocActionId(documentId);
    try {
      await api.approveDocument(documentId, auth.token);
      await reloadDocuments();
      if (id) api.getGarageById(id, auth.token).then(setGarage).catch(() => {});
    } catch (err) {
      setDocumentsError(err instanceof Error ? err.message : 'Failed to approve document');
    } finally {
      setDocActionId(null);
    }
  };

  const handleRejectDocument = async () => {
    if (!auth.token || !rejectingDocId || rejectReason.trim().length < 5) return;
    setDocActionId(rejectingDocId);
    try {
      await api.rejectDocument(rejectingDocId, rejectReason.trim(), auth.token);
      setRejectingDocId(null);
      setRejectReason('');
      await reloadDocuments();
    } catch (err) {
      setDocumentsError(err instanceof Error ? err.message : 'Failed to reject document');
    } finally {
      setDocActionId(null);
    }
  };

  const openMemberDetail = async (subjectId: string) => {
    if (!auth.token) return;
    setNavigatingMemberSubject(subjectId);
    try {
      const detail = await adminUsersApi.getUserDetailBySubject(subjectId, auth.token);
      navigate(`/users/${detail.id}`);
    } catch (err) {
      setMembersError(err instanceof Error ? err.message : 'Failed to open user detail');
    } finally {
      setNavigatingMemberSubject(null);
    }
  };

  const handleApprove = async () => {
    if (!auth.token || !id) return;
    setActionLoading(true);
    setError(null);
    try {
      await api.approveGarage(id, auth.token);
      navigate('/garages');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!auth.token || !id || actionText.length < 10) return;
    setActionLoading(true);
    setError(null);
    try {
      await api.rejectGarage(id, actionText, auth.token);
      navigate('/garages');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestUpdate = async () => {
    if (!auth.token || !id || actionText.length < 10) return;
    setActionLoading(true);
    setError(null);
    try {
      await api.requestGarageUpdate(id, actionText, auth.token);
      navigate('/garages');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request update');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBlockGarage = async () => {
    if (!auth.token || !id) return;
    if (!window.confirm('Block this garage? It will be hidden from customers and cannot accept new bookings until unblocked.')) return;
    setActionLoading(true);
    setError(null);
    try {
      await api.blockGarage(id, auth.token);
      await reloadGarage();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to block garage');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnblockGarage = async () => {
    if (!auth.token || !id) return;
    setActionLoading(true);
    setError(null);
    try {
      await api.unblockGarage(id, auth.token);
      await reloadGarage();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unblock garage');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownload = async (storageKey: string, _filename: string) => {
    if (!auth.token || !id) return;
    try {
      const data = await api.getDownloadUrl(id, storageKey, auth.token);
      window.open(data.url, '_blank');
    } catch {
      setError('Failed to get download URL');
    }
  };

  // ---- Service-tab actions ----

  const handleApproveOffering = async (offeringId: string) => {
    if (!auth.token) return;
    setServiceActionId(offeringId);
    setServicesError(null);
    try {
      await api.approveOffering(offeringId, auth.token);
      await reloadServices();
    } catch (err) {
      setServicesError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setServiceActionId(null);
    }
  };

  const handleRejectOffering = async () => {
    if (!auth.token || !rejectingOfferingId || offeringRejectReason.trim().length < 5) return;
    setServiceActionId(rejectingOfferingId);
    setServicesError(null);
    try {
      await api.rejectOffering(rejectingOfferingId, offeringRejectReason.trim(), auth.token);
      setRejectingOfferingId(null);
      setOfferingRejectReason('');
      await reloadServices();
    } catch (err) {
      setServicesError(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      setServiceActionId(null);
    }
  };

  const handleSetOfferingStatus = async (offeringId: string, status: 'ACTIVE' | 'INACTIVE') => {
    if (!auth.token) return;
    setServiceActionId(offeringId);
    setServicesError(null);
    try {
      await api.changeOfferingStatus(offeringId, status, auth.token);
      await reloadServices();
    } catch (err) {
      setServicesError(err instanceof Error ? err.message : 'Failed to change status');
    } finally {
      setServiceActionId(null);
    }
  };

  const handleBlockOffering = async (offeringId: string) => {
    if (!auth.token) return;
    if (!window.confirm('Block this service? It will disappear from customer search and the partner cannot edit or toggle it until unblocked.')) return;
    setServiceActionId(offeringId);
    setServicesError(null);
    try {
      await api.blockOffering(offeringId, auth.token);
      await reloadServices();
    } catch (err) {
      setServicesError(err instanceof Error ? err.message : 'Failed to block service');
    } finally {
      setServiceActionId(null);
    }
  };

  const handleUnblockOffering = async (offeringId: string) => {
    if (!auth.token) return;
    setServiceActionId(offeringId);
    setServicesError(null);
    try {
      await api.unblockOffering(offeringId, auth.token);
      await reloadServices();
    } catch (err) {
      setServicesError(err instanceof Error ? err.message : 'Failed to unblock service');
    } finally {
      setServiceActionId(null);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-loading-spinner"></div>
        <p style={{ marginTop: '0.75rem', color: 'var(--admin-text-muted)' }}>Loading garage details...</p>
      </div>
    );
  }

  if (error && !garage) {
    return <div className="error-message">{error}</div>;
  }

  if (!garage) return null;

  const isPending = garage.status === 'PENDING';
  const isBlocked = !!garage.blockedAt;
  const canBlock = garage.status !== 'DRAFT';
  // Settlement terms + manual runs are finance-admin work: the tab (and its APIs,
  // gateway-enforced) requires the settlements screen, not the garages screen.
  const showSettlementTab = garage.status === 'APPROVED' && auth.hasScreen('settlements');

  return (
    <div>
      <div className="admin-detail-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <h2 className="admin-detail-title">{garage.name}</h2>
          {garage.ownerUsername && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
              <span style={{ color: 'var(--admin-text-muted)', fontSize: '0.8125rem' }}>Owner</span>
              <UsernameChip username={garage.ownerUsername} userId={garage.ownerUserId} />
            </span>
          )}
          <StatusBadge status={garage.status} />
          {isBlocked && <BlockedBadge />}
          {canEdit && (
            <span
              className="admin-badge admin-badge-role"
              title="You can edit this garage."
            >
              {auth.isSuperAdmin ? 'Super Admin' : 'Garage Editor'}
            </span>
          )}
        </div>
        <button className="admin-btn admin-btn-outline" onClick={() => navigate('/garages')}>
          Back to List
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {isBlocked && (
        <div className="admin-info-banner danger">
          <strong>Blocked:</strong> This garage is currently blocked. It is hidden from customer search and cannot accept new bookings. Existing bookings remain visible.{' '}
          <span data-testid="blocked-by">
            {garage.blockedByName
              ? `Blocked by ${garage.blockedByName} on ${new Date(garage.blockedAt!).toLocaleString()}.`
              : `Blocked at ${new Date(garage.blockedAt!).toLocaleString()}.`}
          </span>
        </div>
      )}
      {garage.adminUpdateNote && (
        <div className="admin-info-banner info">
          <strong>Admin Note:</strong> {garage.adminUpdateNote}
        </div>
      )}
      {garage.rejectionReason && (
        <div className="admin-info-banner danger">
          <strong>Rejection Reason:</strong> {garage.rejectionReason}
        </div>
      )}

      {/* Tab bar */}
      <div
        style={{
          display: 'flex',
          gap: '0.25rem',
          borderBottom: '1px solid var(--admin-border, #e5e7eb)',
          marginBottom: '1rem',
          overflowX: 'auto',
        }}
      >
        <TabButton active={activeTab === 'info'} onClick={() => setActiveTab('info')}>Information</TabButton>
        <TabButton active={activeTab === 'members'} onClick={() => setActiveTab('members')}>Members</TabButton>
        <TabButton active={activeTab === 'documents'} onClick={() => setActiveTab('documents')}>Documents</TabButton>
        <TabButton active={activeTab === 'media'} onClick={() => setActiveTab('media')}>Media</TabButton>
        <TabButton active={activeTab === 'hours'} onClick={() => setActiveTab('hours')}>Operating Hours</TabButton>
        <TabButton active={activeTab === 'services'} onClick={() => setActiveTab('services')}>
          Services{services.length > 0 ? ` (${services.length})` : ''}
        </TabButton>
        <TabButton active={activeTab === 'packages'} onClick={() => setActiveTab('packages')}>Packages</TabButton>
        {showSettlementTab && (
          <TabButton active={activeTab === 'settlement'} onClick={() => setActiveTab('settlement')}>Settlement</TabButton>
        )}
      </div>

      {activeTab === 'info' && (
        <div className="admin-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 className="admin-card-title" style={{ margin: 0 }}>Garage Information</h3>
            {canEdit && (
              <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => setEditDialog('profile')}>
                Edit Profile
              </button>
            )}
          </div>
          <div className="admin-detail-grid">
            <Field label="Garage ID" value={garage.id} />
            <Field label="Owner Username" value={garage.ownerUsername} />
            <Field label="Name" value={garage.name} />
            <Field label="Phone" value={garage.phone} />
            <Field label="Email" value={garage.email} />
            <Field label="License Number" value={garage.licenseNumber} />
            <Field label="City" value={garage.city} />
            <Field label="Area" value={garage.area} />
            <Field label="Street Address" value={garage.streetAddress} />
            <Field label="Manager" value={garage.managerName} />
            <Field label="Car Capacity" value={garage.dailyCapacity} />
            <Field label="Address" value={garage.address} />
            <Field label="Registered" value={garage.createdAt ? new Date(garage.createdAt).toLocaleString() : null} />
            {garage.statusChangedAt && (
              <Field label="Status Changed" value={new Date(garage.statusChangedAt).toLocaleString()} />
            )}
          </div>

          <div style={{ marginTop: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <h4 style={{ margin: 0, color: '#374151', fontSize: '0.9rem', fontWeight: 600 }}>Bank Details</h4>
              {canEdit && (
                <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => setEditDialog('bank')}>
                  Edit Bank Details
                </button>
              )}
            </div>
            <div className="admin-detail-grid">
              <Field label="Account Holder" value={garage.accountHolder} />
              <Field label="Bank Name" value={garage.bankName} />
              <Field label="IBAN" value={garage.iban} />
            </div>
          </div>

          {/* UAE VAT registration — drives customer invoice type + settlement docs (Spec 049/051). */}
          <div style={{ marginTop: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <h4 style={{ margin: 0, color: '#374151', fontSize: '0.9rem', fontWeight: 600 }}>VAT Registration</h4>
              {canEdit && (
                <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => setEditDialog('vat')}>
                  Edit VAT
                </button>
              )}
            </div>
            <div className="admin-detail-grid">
              <Field label="VAT Registered" value={garage.vatRegistered == null ? null : garage.vatRegistered ? 'Yes' : 'No'} />
              <Field label="TRN" value={garage.trn} />
              <Field label="Registered From" value={garage.vatRegisteredFrom} />
              <Field label="Declared At" value={garage.vatStatusDeclaredAt ? new Date(garage.vatStatusDeclaredAt).toLocaleString() : null} />
            </div>
          </div>

          {/* Spec 058 — the Google listing an admin links this garage to. Fetches its own state from
              the admin-only endpoint: the place id is kept off GarageDetailResponse because that DTO
              is also served to garage owners via /api/garages/me. */}
          <GoogleListingSection garageId={garage.id} garageName={garage.name} canEdit={canEdit} />

          {garage.latitude && garage.longitude && (
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ marginBottom: '0.5rem', color: '#374151', fontSize: '0.9rem', fontWeight: 600 }}>Garage Location</h4>
              <div style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                <iframe
                  title="Garage Location"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps?q=${garage.latitude},${garage.longitude}&z=15&output=embed`}
                />
              </div>
              <p style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#6b7280' }}>
                Coordinates: {garage.latitude}, {garage.longitude}
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'members' && (
        <div className="admin-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 className="admin-card-title" style={{ margin: 0 }}>Members</h3>
            {canEdit && (
              <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => setEditDialog('addMember')}>
                Add Member
              </button>
            )}
          </div>
          {membersError && <div className="error-message" style={{ marginBottom: '0.75rem' }}>{membersError}</div>}
          {members.length === 0 ? (
            <div className="admin-empty-state" style={{ padding: '1.5rem 1rem' }}>
              <p>No members</p>
            </div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table admin-table-members">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Notifications</th>
                    <th>Joined</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id}>
                      <td><UsernameChip username={m.username} compact /></td>
                      <td style={{ fontWeight: 500 }}>{m.name || '—'}</td>
                      <td>{m.email || '—'}</td>
                      <td>{m.phone || '—'}</td>
                      <td><span className={`admin-badge ${m.role === 'OWNER' ? 'garage-owner' : ''}`}>{m.role}</span></td>
                      <td><span className={`admin-badge ${m.active ? 'approved' : 'rejected'}`}>{m.active ? 'Active' : 'Inactive'}</span></td>
                      <td><MemberNotificationBadges member={m} /></td>
                      <td>{new Date(m.createdAt).toLocaleDateString()}</td>
                      <td className="admin-table-members-actions">
                        <div style={{ display: 'flex', gap: '0.25rem' }}>
                          <button
                            className="admin-btn admin-btn-primary"
                            style={{ padding: '0.25rem 0.75rem', fontSize: '0.8125rem' }}
                            disabled={navigatingMemberSubject === m.userSubjectId}
                            onClick={() => openMemberDetail(m.userSubjectId)}
                          >
                            {navigatingMemberSubject === m.userSubjectId ? 'Opening…' : 'View'}
                          </button>
                          {canEdit && m.role !== 'OWNER' && (
                            <button
                              className="admin-btn admin-btn-danger"
                              style={{ padding: '0.25rem 0.75rem', fontSize: '0.8125rem' }}
                              onClick={() => handleRemoveMember(m.id, m.name)}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'hours' && (
        <div className="admin-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 className="admin-card-title" style={{ margin: 0 }}>Operating Hours</h3>
            {canEdit && (
              <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => setEditDialog('hours')}>
                Edit Hours
              </button>
            )}
          </div>
          {garage.operatingHours.length === 0 ? (
            <div className="admin-empty-state" style={{ padding: '1.5rem 1rem' }}>
              <p>No operating hours set</p>
            </div>
          ) : (
            <table className="admin-hours-table">
              <thead>
                <tr><th>Day</th><th>Status</th><th>Hours</th></tr>
              </thead>
              <tbody>
                {garage.operatingHours.map((h) => (
                  <tr key={h.id}>
                    <td>{DAY_NAMES[h.dayOfWeek] || `Day ${h.dayOfWeek}`}</td>
                    <td>{h.isClosed ? 'Closed' : 'Open'}</td>
                    <td>{h.isClosed ? '-' : formatHours(h.openTime, h.closeTime)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'documents' && (
        <>
          {/* Single unified Documents card: garage_documents rows (trade license,
              Emirates ID, others) plus a synthesised trade-license row sourced from
              garage.media when no TRADE_LICENSE document row exists yet (pre-approval
              partners upload the license through the media flow, which writes to
              garage.licenseStorageKey but not to garage_documents). */}
          <div className="admin-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '0.5rem', flexWrap: 'wrap' }}>
              <h3 className="admin-card-title" style={{ margin: 0 }}>Documents</h3>
              {canEdit && (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <label className="admin-btn admin-btn-outline admin-btn-sm" style={{ cursor: 'pointer', margin: 0 }}>
                    {uploadBusy === 'doc:TRADE_LICENSE' ? 'Uploading…' : 'Upload Trade License'}
                    <input
                      type="file"
                      style={{ display: 'none' }}
                      accept="application/pdf,image/*"
                      disabled={uploadBusy !== null}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) adminUploadAndConfirm(f, 'TRADE_LICENSE', 'TRADE_LICENSE');
                        e.target.value = '';
                      }}
                    />
                  </label>
                  <label className="admin-btn admin-btn-outline admin-btn-sm" style={{ cursor: 'pointer', margin: 0 }}>
                    {uploadBusy === 'doc:OWNER_EMIRATES_ID' ? 'Uploading…' : 'Upload Owner Emirates ID'}
                    <input
                      type="file"
                      style={{ display: 'none' }}
                      accept="application/pdf,image/*"
                      disabled={uploadBusy !== null}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) adminUploadAndConfirm(f, 'TRADE_LICENSE', 'OWNER_EMIRATES_ID');
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>
              )}
            </div>
            {documentsError && <div className="error-message" style={{ marginBottom: '0.75rem' }}>{documentsError}</div>}
            {(() => {
              const hasTradeLicenseDocRow = documents.some((d) => d.documentType === 'TRADE_LICENSE');
              const tradeLicenseMedia = !hasTradeLicenseDocRow
                ? garage.media.find((m) => m.mediaType === 'TRADE_LICENSE')
                : undefined;
              const hasAnyDoc = documents.length > 0 || !!tradeLicenseMedia;
              if (!hasAnyDoc) {
                return (
                  <div className="admin-empty-state" style={{ padding: '1.5rem 1rem' }}>
                    <p>No documents uploaded yet</p>
                  </div>
                );
              }
              return (
              <table className="admin-data-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Status</th>
                    <th>License #</th>
                    <th>File</th>
                    <th>Uploaded</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tradeLicenseMedia && (
                    <tr key={`media-${tradeLicenseMedia.id}`}>
                      <td>Trade License</td>
                      <td>
                        <span className="admin-badge pending">PENDING</span>
                      </td>
                      <td>{garage.licenseNumber || '—'}</td>
                      <td>
                        <button
                          type="button"
                          onClick={() => handleDownload(tradeLicenseMedia.storageKey, tradeLicenseMedia.originalFilename)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: '#2196f3',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            font: 'inherit',
                          }}
                        >
                          {tradeLicenseMedia.originalFilename || 'View'}
                        </button>
                      </td>
                      <td>{new Date(tradeLicenseMedia.createdAt).toLocaleString()}</td>
                      <td style={{ textAlign: 'right', color: '#6b7280', fontSize: '0.875rem' }}>
                        Approved with garage
                      </td>
                    </tr>
                  )}
                  {documents.map((d) => (
                    <tr key={d.id}>
                      <td>{
                        d.documentType === 'TRADE_LICENSE' ? 'Trade License'
                        : d.documentType === 'OWNER_EMIRATES_ID' ? 'Owner Emirates ID'
                        : 'Other'
                      }</td>
                      <td>
                        <span className={`admin-badge ${d.status === 'APPROVED' ? 'approved'
                          : d.status === 'PENDING' ? 'pending'
                          : d.status === 'REJECTED' ? 'rejected'
                          : 'update-requested'}`}>
                          {d.status}
                        </span>
                      </td>
                      <td>{d.licenseNumber || '—'}</td>
                      <td>
                        {d.downloadUrl
                          ? <a href={d.downloadUrl} target="_blank" rel="noreferrer">{d.originalFilename || 'View'}</a>
                          : '—'}
                      </td>
                      <td>{new Date(d.createdAt).toLocaleString()}</td>
                      <td style={{ textAlign: 'right' }}>
                        {(d.documentType === 'TRADE_LICENSE' || d.documentType === 'OWNER_EMIRATES_ID') && d.status === 'PENDING' && (
                          <>
                            <button
                              className="admin-btn admin-btn-primary admin-btn-sm"
                              disabled={docActionId === d.id}
                              onClick={() => handleApproveDocument(d.id)}
                            >
                              {docActionId === d.id ? 'Working...' : 'Approve'}
                            </button>
                            <button
                              className="admin-btn admin-btn-outline admin-btn-sm"
                              style={{ marginInlineStart: '0.5rem' }}
                              disabled={docActionId === d.id}
                              onClick={() => { setRejectingDocId(d.id); setRejectReason(''); }}
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {d.status === 'REJECTED' && d.rejectionReason && (
                          <span style={{ color: 'var(--admin-text-muted)', fontSize: '0.85rem' }}>
                            Reason: {d.rejectionReason}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              );
            })()}

            {rejectingDocId && (
              <div className="admin-action-form" style={{ marginTop: '1rem' }}>
                <h4 style={{ marginBottom: '0.5rem' }}>Reject Document</h4>
                <textarea
                  rows={3}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Explain the rejection (min 5 characters)"
                  style={{ width: '100%' }}
                />
                <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button
                    className="admin-btn admin-btn-outline admin-btn-sm"
                    onClick={() => { setRejectingDocId(null); setRejectReason(''); }}
                  >
                    Cancel
                  </button>
                  <button
                    className="admin-btn admin-btn-danger admin-btn-sm"
                    disabled={rejectReason.trim().length < 5 || docActionId === rejectingDocId}
                    onClick={handleRejectDocument}
                  >
                    {docActionId === rejectingDocId ? 'Working...' : 'Confirm Reject'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Spec 074 — Signed / Notified / Not signed + the terms update notice. A send or refresh
              remounts the Signed agreements card so a new NOTIFIED record shows. */}
          <TermsStatusCard garageId={garage.id} onChanged={() => setTermsRefreshKey((k) => k + 1)} />

          {/* Spec 071 — replaces the old "Terms Agreement Status" card. */}
          <SignedAgreementsCard key={termsRefreshKey} garageId={garage.id} />
        </>
      )}

      {activeTab === 'media' && (() => {
        const logoMedia = garage.media.find((m) => m.mediaType === 'LOGO');
        // Order by sortOrder — the same key the partner portal and the customer app use.
        // This sorted by createdAt while claiming below to show exactly what the app sees,
        // which was only true by accident.
        const gallery = garage.media
          .filter((m) => m.mediaType === 'GALLERY_PHOTO')
          .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
            || (a.createdAt || '').localeCompare(b.createdAt || ''));
        return (
          <div className="admin-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
              <h3 className="admin-card-title" style={{ margin: 0 }}>Garage Media</h3>
              {canEdit && (
                <label className="admin-btn admin-btn-outline admin-btn-sm" style={{ cursor: 'pointer', margin: 0 }}>
                  {uploadBusy === 'media:GALLERY_PHOTO' ? 'Uploading…' : 'Add Gallery Photo'}
                  <input
                    type="file"
                    style={{ display: 'none' }}
                    accept="image/*"
                    disabled={uploadBusy !== null}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) adminUploadAndConfirm(f, 'GALLERY_PHOTO');
                      e.target.value = '';
                    }}
                  />
                </label>
              )}
            </div>
            <p style={{ color: '#6b7280', fontSize: '0.8125rem', margin: '0 0 1.25rem' }}>
              The gallery in the order customers see it. The logo is the garage's cover image and
              is not part of the gallery. Documents are not shown to customers.
            </p>

            <section style={{ marginBottom: '1.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <h4 style={{ margin: 0, color: '#374151', fontSize: '0.9rem', fontWeight: 600 }}>Logo</h4>
                {canEdit && (
                  <label className="admin-btn admin-btn-outline admin-btn-sm" style={{ cursor: 'pointer', margin: 0 }}>
                    {uploadBusy === 'media:LOGO' ? 'Uploading…' : (logoMedia ? 'Replace Logo' : 'Upload Logo')}
                    <input
                      type="file"
                      style={{ display: 'none' }}
                      accept="image/*"
                      disabled={uploadBusy !== null}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) adminUploadAndConfirm(f, 'LOGO');
                        e.target.value = '';
                      }}
                    />
                  </label>
                )}
              </div>
              {logoMedia && logoMedia.downloadUrl ? (
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                  <img
                    src={logoMedia.downloadUrl}
                    alt="Garage logo"
                    style={{ width: 144, height: 144, borderRadius: '12px', objectFit: 'cover', border: '1px solid #e5e7eb', background: '#fff' }}
                  />
                  {canEdit && (
                    <button
                      type="button"
                      className="admin-btn admin-btn-danger admin-btn-sm"
                      onClick={() => handleDeleteMedia(logoMedia.id)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              ) : (
                <div className="admin-empty-state" style={{ padding: '1.25rem 1rem' }}>
                  <p>No logo uploaded</p>
                </div>
              )}
            </section>

            <section>
              <h4 style={{ marginBottom: '0.5rem', color: '#374151', fontSize: '0.9rem', fontWeight: 600 }}>
                Gallery Photos{gallery.length > 0 ? ` (${gallery.length})` : ''}
              </h4>
              {gallery.length === 0 ? (
                <div className="admin-empty-state" style={{ padding: '1.25rem 1rem' }}>
                  <p>No gallery photos</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.75rem' }}>
                  {gallery.map((m, index) => (
                    <div key={m.id} style={{ position: 'relative', aspectRatio: '1 / 1', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                      <a href={m.downloadUrl ?? undefined} target="_blank" rel="noreferrer" style={{ display: 'block', width: '100%', height: '100%' }}>
                        {m.downloadUrl ? (
                          <img src={m.downloadUrl} alt="Gallery" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '0.75rem' }}>No URL</div>
                        )}
                      </a>
                      {canEdit && (
                        <button
                          type="button"
                          className="admin-btn admin-btn-danger admin-btn-sm"
                          onClick={() => handleDeleteMedia(m.id)}
                          style={{ position: 'absolute', top: '0.25rem', right: '0.25rem', padding: '0.125rem 0.5rem', fontSize: '0.75rem' }}
                        >
                          ×
                        </button>
                      )}
                      {canEdit && gallery.length > 1 && (
                        <div className="v2-photo-tile-order">
                          <button
                            type="button"
                            className="v2-photo-move"
                            disabled={index === 0 || mediaReordering}
                            aria-label={`Move photo ${index + 1} earlier`}
                            title="Move earlier"
                            onClick={() => moveGalleryPhoto(gallery, index, -1)}
                          >‹</button>
                          <span className="v2-photo-tile-position">{index + 1}</span>
                          <button
                            type="button"
                            className="v2-photo-move"
                            disabled={index === gallery.length - 1 || mediaReordering}
                            aria-label={`Move photo ${index + 1} later`}
                            title="Move later"
                            onClick={() => moveGalleryPhoto(gallery, index, 1)}
                          >›</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        );
      })()}

      {activeTab === 'services' && (
        <div className="admin-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 className="admin-card-title" style={{ margin: 0 }}>Services</h3>
            <div style={{ display: 'inline-flex', gap: '0.5rem' }}>
              <button
                type="button"
                className="admin-btn admin-btn-outline admin-btn-sm"
                onClick={() => navigate(`/garages/${garage.id}/services`)}
                title="Open the full Services Workspace"
              >
                Open Workspace
              </button>
              {canEdit && (
                <button
                  type="button"
                  className="admin-btn admin-btn-primary admin-btn-sm"
                  onClick={() => setEditDialog('addOffering')}
                >
                  + Add Service
                </button>
              )}
            </div>
          </div>
          {servicesError && <div className="error-message" style={{ marginBottom: '0.75rem' }}>{servicesError}</div>}
          {services.length === 0 ? (
            <div className="admin-empty-state" style={{ padding: '1.5rem 1rem' }}>
              <p>No services added yet</p>
            </div>
          ) : (
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Category</th>
                    <th>Price</th>
                    <th>Duration</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((s) => {
                    const blocked = !!s.blockedAt;
                    const busy = serviceActionId === s.id;
                    const mins = s.durationMinutes || 0;
                    const d = Math.floor(mins / (24 * 60));
                    const h = Math.floor((mins % (24 * 60)) / 60);
                    const durationLabel = mins ? `${d ? `${d}d ` : ''}${h ? `${h}h` : (d ? '' : '0h')}`.trim() : '—';
                    const categoryLabel = s.category?.parentName
                      ? `${s.category.parentName} › ${s.category.name}`
                      : (s.category?.name || '—');
                    return (
                      <tr key={s.id}>
                        <td style={{ fontWeight: 500 }}>{s.name || '—'}</td>
                        <td>{categoryLabel}</td>
                        <td>{s.price != null ? `${s.price} AED` : '—'}</td>
                        <td>{durationLabel}</td>
                        <td><OfferingStatusBadge status={s.status} blockedAt={s.blockedAt} /></td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', gap: '0.25rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            <button
                              className="admin-btn admin-btn-outline admin-btn-sm"
                              onClick={() => navigate(`/garages/${garage.id}/services`)}
                            >
                              View
                            </button>
                            {canEdit && (
                              <button
                                className="admin-btn admin-btn-outline admin-btn-sm"
                                onClick={() => setEditingOffering(s)}
                              >
                                Edit
                              </button>
                            )}
                            {!blocked && s.status === 'PENDING_APPROVAL' && (
                              <>
                                <button
                                  className="admin-btn admin-btn-success admin-btn-sm"
                                  disabled={busy}
                                  onClick={() => handleApproveOffering(s.id)}
                                >
                                  {busy ? '…' : 'Approve'}
                                </button>
                                <button
                                  className="admin-btn admin-btn-outline admin-btn-sm"
                                  disabled={busy}
                                  onClick={() => { setRejectingOfferingId(s.id); setOfferingRejectReason(''); }}
                                >
                                  Reject
                                </button>
                              </>
                            )}
                            {!blocked && s.status === 'ACTIVE' && (
                              <button
                                className="admin-btn admin-btn-outline admin-btn-sm"
                                disabled={busy}
                                onClick={() => handleSetOfferingStatus(s.id, 'INACTIVE')}
                              >
                                {busy ? '…' : 'Deactivate'}
                              </button>
                            )}
                            {!blocked && s.status === 'INACTIVE' && (
                              <button
                                className="admin-btn admin-btn-success admin-btn-sm"
                                disabled={busy}
                                onClick={() => handleSetOfferingStatus(s.id, 'ACTIVE')}
                              >
                                {busy ? '…' : 'Activate'}
                              </button>
                            )}
                            {!blocked ? (
                              <button
                                className="admin-btn admin-btn-danger admin-btn-sm"
                                disabled={busy}
                                onClick={() => handleBlockOffering(s.id)}
                              >
                                {busy ? '…' : 'Block'}
                              </button>
                            ) : (
                              <button
                                className="admin-btn admin-btn-success admin-btn-sm"
                                disabled={busy}
                                onClick={() => handleUnblockOffering(s.id)}
                              >
                                {busy ? '…' : 'Unblock'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {rejectingOfferingId && (
            <div className="admin-action-form" style={{ marginTop: '1rem' }}>
              <h4 style={{ marginBottom: '0.5rem' }}>Reject Service</h4>
              <textarea
                rows={3}
                value={offeringRejectReason}
                onChange={(e) => setOfferingRejectReason(e.target.value)}
                placeholder="Explain the rejection (min 5 characters)"
                style={{ width: '100%' }}
              />
              <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                  className="admin-btn admin-btn-outline admin-btn-sm"
                  onClick={() => { setRejectingOfferingId(null); setOfferingRejectReason(''); }}
                >
                  Cancel
                </button>
                <button
                  className="admin-btn admin-btn-danger admin-btn-sm"
                  disabled={offeringRejectReason.trim().length < 5 || serviceActionId === rejectingOfferingId}
                  onClick={handleRejectOffering}
                >
                  {serviceActionId === rejectingOfferingId ? 'Working...' : 'Confirm Reject'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Spec 081b — the garage's packages (all statuses); each row opens the review page. */}
      {activeTab === 'packages' && auth.token && (
        <GaragePackagesTab garageId={garage.id} token={auth.token} />
      )}

      {activeTab === 'settlement' && showSettlementTab && (
        <>
          <div className="admin-card">
            <h3 className="admin-card-title" style={{ marginBottom: '1rem' }}>Settlement Terms</h3>
            <SettlementConfigForm garageId={garage.id} />
          </div>
          <AwaitingTabbyCard garageId={garage.id} />
          <RunSettlementCard garageId={garage.id} />
        </>
      )}

      {/* Admin Actions — always visible below tabs */}
      <div className="admin-card">
        <h3 className="admin-card-title" style={{ marginBottom: '1rem' }}>Admin Actions</h3>

        {!activeAction ? (
          <div className="admin-action-buttons" style={{ flexWrap: 'wrap' }}>
            {isPending && (
              <>
                <button
                  className="admin-btn admin-btn-success admin-btn-lg"
                  disabled={actionLoading}
                  onClick={handleApprove}
                >
                  {actionLoading ? 'Processing...' : 'Approve'}
                </button>
                <button
                  className="admin-btn admin-btn-danger admin-btn-lg"
                  disabled={actionLoading}
                  onClick={() => { setActiveAction('reject'); setActionText(''); }}
                >
                  Reject
                </button>
                <button
                  className="admin-btn admin-btn-warning admin-btn-lg"
                  disabled={actionLoading}
                  onClick={() => { setActiveAction('request-update'); setActionText(''); }}
                >
                  Request Update
                </button>
              </>
            )}
            {canBlock && !isBlocked && (
              <button
                className="admin-btn admin-btn-danger admin-btn-lg"
                disabled={actionLoading}
                onClick={handleBlockGarage}
              >
                {actionLoading ? 'Processing...' : 'Block Garage'}
              </button>
            )}
            {isBlocked && (
              <button
                className="admin-btn admin-btn-success admin-btn-lg"
                disabled={actionLoading}
                onClick={handleUnblockGarage}
              >
                {actionLoading ? 'Processing...' : 'Unblock Garage'}
              </button>
            )}
            {!isPending && !canBlock && (
              <p style={{ color: 'var(--admin-text-muted)' }}>No actions available for this garage in its current state.</p>
            )}
          </div>
        ) : (
          <div className="admin-action-panel">
            <h3>
              {activeAction === 'reject' ? 'Rejection Reason' : 'Update Request Note'}
              <span style={{ fontWeight: 400, fontSize: '0.8125rem', color: 'var(--admin-text-muted)', marginLeft: '0.5rem' }}>
                (min 10 characters)
              </span>
            </h3>
            <textarea
              className="admin-textarea"
              value={actionText}
              onChange={(e) => setActionText(e.target.value)}
              placeholder={activeAction === 'reject' ? 'Provide a reason for rejection...' : 'Describe what needs to be updated...'}
              rows={3}
            />
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem' }}>
              <button
                className={`admin-btn admin-btn-lg ${activeAction === 'reject' ? 'admin-btn-danger' : 'admin-btn-warning'}`}
                disabled={actionLoading || actionText.length < 10}
                onClick={activeAction === 'reject' ? handleReject : handleRequestUpdate}
              >
                {actionLoading ? 'Processing...' : activeAction === 'reject' ? 'Confirm Rejection' : 'Send Update Request'}
              </button>
              <button
                className="admin-btn admin-btn-outline admin-btn-lg"
                onClick={() => { setActiveAction(null); setActionText(''); }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Spec 038 — admin edit dialogs */}
      {editDialog === 'profile' && garage && (
        <EditGarageProfileDialog
          garage={garage}
          onClose={() => setEditDialog(null)}
          onSaved={reloadGarage}
        />
      )}
      {editDialog === 'hours' && garage && (
        <EditGarageHoursDialog
          garageId={garage.id}
          initial={garage.operatingHours.map((h) => ({
            dayOfWeek: h.dayOfWeek,
            openTime: h.openTime ?? '09:00',
            closeTime: h.closeTime ?? '18:00',
            isClosed: h.isClosed,
          }))}
          onClose={() => setEditDialog(null)}
          onSaved={reloadGarage}
        />
      )}
      {editDialog === 'addMember' && garage && (
        <AddGarageMemberDialog
          garageId={garage.id}
          onClose={() => setEditDialog(null)}
          onSaved={async () => {
            if (auth.token && id) {
              const refreshed = await api.getGarageMembers(id, auth.token);
              setMembers(refreshed);
            }
          }}
        />
      )}
      {editDialog === 'bank' && garage && (
        <EditGarageBankDetailsDialog
          garageId={garage.id}
          current={{ accountHolder: garage.accountHolder, bankName: garage.bankName, iban: garage.iban }}
          onClose={() => setEditDialog(null)}
          onSaved={reloadGarage}
        />
      )}
      {editDialog === 'vat' && garage && (
        <EditGarageVatDialog
          garageId={garage.id}
          current={{ vatRegistered: garage.vatRegistered, trn: garage.trn, vatRegisteredFrom: garage.vatRegisteredFrom }}
          onClose={() => setEditDialog(null)}
          onSaved={reloadGarage}
        />
      )}
      {editingOffering && garage && auth.token && (
        <AddServiceModalV2
          garageId={garage.id}
          token={auth.token}
          editOfferingId={editingOffering.id}
          onClose={() => setEditingOffering(null)}
          onSaved={() => { setEditingOffering(null); reloadServices(); }}
        />
      )}
      {editDialog === 'addOffering' && garage && auth.token && (
        <AddServiceModalV2
          garageId={garage.id}
          token={auth.token}
          onClose={() => setEditDialog(null)}
          onSaved={() => { setEditDialog(null); reloadServices(); }}
        />
      )}
    </div>
  );
}
